import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ArrowUp, Loader2, Mic, Plus, X } from 'lucide-react';
import { Button } from '../ui/button';
import { chatLucideIconProps } from './chat-ui';
import {
  getComposerPrimaryButtonMode,
  shouldSubmitComposerOnKeyDown,
} from './chat-composer.logic';
import {
  COMPOSER_TEXTAREA_MAX_HEIGHT_PX,
  COMPOSER_TEXTAREA_MIN_HEIGHT_PX,
} from './chat-composer/constants';
import { ChatComposerAttachmentChip } from './chat-composer/attachment-chip';
import { ChatComposerVoiceActivity } from './chat-composer/voice-activity';
import type { ChatComposerAttachment } from './chat-composer/types';
import { useChatComposerVoiceInput } from './chat-composer.voice';
import { ArrowTurnDownRightSFSymbolMedium } from '@/assets/sf-symbols/medium/arrow.turn.down.right';

export type { ChatComposerAttachment } from './chat-composer/types';

export function ChatComposer({
  backgroundColor,
  value,
  disabled,
  isSubmitting,
  canCancel,
  canSubmit,
  draftQuote,
  attachments,
  footerCaption,
  readOnly = false,
  allowKeyboardSubmit = true,
  primaryButtonClassName,
  placeholder = 'Ask Bridge to search, summarize, reason, or browse…',
  allowAttachments = true,
  allowVoice = true,
  focusRequestKey,
  onChange,
  onSubmit,
  onCancel,
  onRemoveQuote,
  onActivateQuote,
  onPickFiles,
  onRemoveAttachment,
}: {
  backgroundColor?: string;
  value: string;
  disabled: boolean;
  isSubmitting: boolean;
  canCancel: boolean;
  canSubmit: boolean;
  draftQuote?: { text: string } | null;
  attachments: ChatComposerAttachment[];
  footerCaption?: ReactNode;
  readOnly?: boolean;
  allowKeyboardSubmit?: boolean;
  primaryButtonClassName?: string;
  placeholder?: string;
  allowAttachments?: boolean;
  allowVoice?: boolean;
  focusRequestKey?: number;
  onChange: (value: string) => void;
  onSubmit: (options?: { textOverride?: string }) => Promise<void> | void;
  onCancel: () => void;
  onRemoveQuote?: () => void;
  onActivateQuote?: () => void;
  onPickFiles: (files: File[]) => void;
  onRemoveAttachment: (id: string) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const appliedFocusRequestKeyRef = useRef<number | undefined>(undefined);
  const [isComposing, setIsComposing] = useState(false);
  const {
    voiceState,
    liveLevelRef,
    voiceErrorMessage,
    clearVoiceErrorMessage,
    canStartVoiceRecording,
    canAutoSendVoiceRecording,
    canRetryVoiceTranscription,
    requestStartVoiceRecording,
    requestStopVoiceRecording,
    requestSendVoiceRecording,
    requestCancelVoiceInput,
    requestRetryVoiceTranscription,
  } = useChatComposerVoiceInput({
    draft: value,
    disabled,
    canCancel,
    isSubmitting,
    attachments,
    onDraftChange: onChange,
    onSubmit,
  });

  const isRecordingVoice = voiceState.phase === 'recording';
  const isTranscribingVoice = voiceState.phase === 'transcribing';
  const voiceActivityVisible = isRecordingVoice || isTranscribingVoice;
  const isTextareaDisabled = disabled || isSubmitting || isTranscribingVoice;
  const primaryButtonMode = voiceActivityVisible
    ? 'submit'
    : getComposerPrimaryButtonMode({
        canCancel,
        isSubmitting,
      });

  const isPrimaryButtonDisabled = isRecordingVoice
    ? !canAutoSendVoiceRecording
    : isTranscribingVoice
      ? true
      : canCancel
        ? false
        : disabled || !canSubmit || isSubmitting;

  const focusTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (!el || el.disabled || readOnly) {
      return false;
    }
    const wasActive = document.activeElement === el;
    el.focus({ preventScroll: true });
    if (!wasActive) {
      const cursorPosition = el.value.length;
      el.setSelectionRange(cursorPosition, cursorPosition);
    }
    return document.activeElement === el;
  }, [readOnly]);

  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) {
      return;
    }
    el.style.height = 'auto';
    const next = Math.min(
      Math.max(el.scrollHeight, COMPOSER_TEXTAREA_MIN_HEIGHT_PX),
      COMPOSER_TEXTAREA_MAX_HEIGHT_PX
    );
    el.style.height = `${next}px`;
  }, [value]);

  useEffect(() => {
    if (voiceState.phase === 'idle') {
      focusTextarea();
    }
  }, [focusTextarea, voiceState.phase]);

  useLayoutEffect(() => {
    if (
      focusRequestKey === undefined ||
      appliedFocusRequestKeyRef.current === focusRequestKey ||
      isTextareaDisabled
    ) {
      return;
    }
    if (focusTextarea()) {
      appliedFocusRequestKeyRef.current = focusRequestKey;
    }
  }, [focusRequestKey, focusTextarea, isTextareaDisabled]);

  const primaryButtonAriaLabel = isRecordingVoice
    ? 'Send recorded message'
    : isTranscribingVoice
      ? 'Transcribing voice input'
      : canCancel
        ? 'Stop generation'
        : 'Send message';

  const handlePrimaryButtonClick = () => {
    if (isRecordingVoice) {
      requestSendVoiceRecording();
      return;
    }

    if (isTranscribingVoice) {
      return;
    }

    if (canCancel) {
      onCancel();
      return;
    }

    if (!isPrimaryButtonDisabled) {
      void onSubmit();
    }
  };

  return (
    <div
      data-chat-composer-root="true"
      className="rounded-[22px] border border-border/80 bg-chat-composer-bg px-2.5 py-2 shadow-[0_1px_2px_rgba(23,23,23,0.05),0_14px_34px_rgba(23,23,23,0.08)] transition-[border-color,box-shadow] focus-within:border-foreground/15 focus-within:shadow-[0_1px_3px_rgba(23,23,23,0.06),0_18px_42px_rgba(23,23,23,0.12)]"
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        multiple
        onChange={event => {
          const files = Array.from(event.target.files ?? []);
          if (files.length > 0) {
            onPickFiles(files);
          }
          event.target.value = '';
        }}
      />

      {draftQuote?.text ? (
        <div className="mb-2">
          <div className="flex items-center gap-2 rounded-[12px] bg-muted/70 px-3 py-2 text-xs text-muted-foreground">
            <button
              type="button"
              className="flex min-w-0 flex-1 items-center gap-2 text-left"
              onClick={onActivateQuote}
            >
              <span className="shrink-0 leading-none">
                <ArrowTurnDownRightSFSymbolMedium className="text-[8px]" />
              </span>
              <span className="truncate">{draftQuote.text}</span>
            </button>
            <button
              type="button"
              onClick={onRemoveQuote}
              className="inline-flex size-5 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-background/80 hover:text-foreground"
              aria-label="Remove quote"
            >
              <X {...chatLucideIconProps} className="size-3.5" />
            </button>
          </div>
        </div>
      ) : null}

      {attachments.length > 0 ? (
        <div className="mb-2 flex flex-wrap gap-2 px-1">
          {attachments.map(attachment => (
            <ChatComposerAttachmentChip
              key={attachment.id}
              attachment={attachment}
              disabled={disabled || isSubmitting}
              onRemove={onRemoveAttachment}
            />
          ))}
        </div>
      ) : null}

      {voiceErrorMessage ? (
        <div className="mb-2 flex items-start gap-2 rounded-2xl border border-rose-200/80 bg-rose-50/90 px-3 py-2 text-sm text-rose-700 dark:border-rose-400/30 dark:bg-rose-500/10 dark:text-rose-200">
          <div className="min-w-0 flex-1">{voiceErrorMessage}</div>
          {canRetryVoiceTranscription ? (
            <button
              type="button"
              onClick={requestRetryVoiceTranscription}
              className="shrink-0 rounded-full border border-rose-300/80 px-2.5 py-1 text-xs font-medium text-rose-700 transition-colors hover:bg-rose-100 dark:border-rose-300/40 dark:text-rose-100 dark:hover:bg-rose-500/15"
              aria-label="Retry voice transcription"
            >
              Retry
            </button>
          ) : null}
          <button
            type="button"
            onClick={clearVoiceErrorMessage}
            className="inline-flex size-6 shrink-0 items-center justify-center rounded-full text-rose-500 transition-colors hover:bg-rose-100 hover:text-rose-700 dark:text-rose-300 dark:hover:bg-rose-500/15 dark:hover:text-rose-100"
            aria-label="Dismiss voice error"
          >
            <X {...chatLucideIconProps} className="size-4" />
          </button>
        </div>
      ) : null}

      <textarea
        ref={textareaRef}
        value={value}
        onChange={event => onChange(event.target.value)}
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={() => setIsComposing(false)}
        rows={1}
        placeholder={placeholder}
        readOnly={readOnly}
        disabled={isTextareaDisabled}
        style={{ maxHeight: COMPOSER_TEXTAREA_MAX_HEIGHT_PX }}
        data-chat-composer-input="true"
        className="chat-type-body min-h-[48px] w-full resize-none overflow-y-auto overflow-x-hidden bg-transparent px-1.5 py-1 text-foreground outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
        onKeyDown={event => {
          if (
            !shouldSubmitComposerOnKeyDown({
              key: event.key,
              shiftKey: event.shiftKey,
              isComposing,
              nativeIsComposing: event.nativeEvent.isComposing,
              nativeKeyCode: event.nativeEvent.keyCode,
            })
          ) {
            return;
          }

          event.preventDefault();

          if (isRecordingVoice) {
            if (canAutoSendVoiceRecording) {
              requestSendVoiceRecording();
            }
            return;
          }

          if (isTranscribingVoice) {
            return;
          }

          if (allowKeyboardSubmit && canSubmit && !disabled && !isSubmitting) {
            void onSubmit();
          }
        }}
      />

      <div className="mt-2 flex items-end justify-between gap-2">
        {voiceActivityVisible ? (
          <ChatComposerVoiceActivity
            phase={voiceState.phase}
            duration={voiceState.duration}
            liveLevelRef={liveLevelRef}
            onCancel={requestCancelVoiceInput}
            onStopRecording={requestStopVoiceRecording}
          />
        ) : (
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            {allowAttachments ? (
              <button
                type="button"
                className="inline-flex size-8 shrink-0 items-center justify-center rounded-full border border-border/70 bg-background text-muted-foreground shadow-none transition-colors hover:bg-muted/50 hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
                onClick={() => {
                  fileInputRef.current?.click();
                }}
                disabled={disabled || isSubmitting}
                aria-label="Upload files"
              >
                <Plus {...chatLucideIconProps} className="size-4" />
              </button>
            ) : null}
            {footerCaption ? (
              <div className="chat-type-caption flex min-w-0 items-center gap-1.5 truncate px-1 text-muted-foreground">
                {footerCaption}
              </div>
            ) : null}
          </div>
        )}

        <div className="flex shrink-0 items-center gap-2 self-end">
          {allowVoice && !voiceActivityVisible ? (
            <button
              type="button"
              onClick={requestStartVoiceRecording}
              disabled={!canStartVoiceRecording}
              className="inline-flex size-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
              aria-label="Record voice input"
            >
              <Mic {...chatLucideIconProps} className="size-4" />
            </button>
          ) : null}
          <Button
            size="icon"
            onClick={handlePrimaryButtonClick}
            disabled={isPrimaryButtonDisabled}
            variant="default"
            className={`size-9 rounded-full shadow-none disabled:bg-muted disabled:text-muted-foreground ${primaryButtonClassName ?? ''}`}
            aria-label={primaryButtonAriaLabel}
          >
            {primaryButtonMode === 'cancel' ? (
              <span
                aria-hidden="true"
                className="block size-3 rounded-[2px] bg-current"
              />
            ) : primaryButtonMode === 'submitting' ? (
              <Loader2
                {...chatLucideIconProps}
                className="size-4 animate-spin"
              />
            ) : (
              <ArrowUp {...chatLucideIconProps} className="size-[18px]" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
